import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Props = {
  open: boolean;
  onAccepted: () => Promise<void>;
};

const ImageUploadConsentDialog: React.FC<Props> = ({ open, onAccepted }) => {
  const { t } = useTranslation();
  const rawId = useId();
  const titleId = `upload-consent-title-${rawId.replace(/:/g, "")}`;
  const panelRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<HTMLElement | null>(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setChecked(false);
      setError(null);
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    prevFocus.current = document.activeElement as HTMLElement | null;

    const focusFirst = () => {
      const panel = panelRef.current;
      const els = panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : [];
      els[0]?.focus();
    };
    focusFirst();
    requestAnimationFrame(focusFirst);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (open) return;
    const el = prevFocus.current;
    prevFocus.current = null;
    if (el && typeof el.focus === "function") window.setTimeout(() => el.focus(), 0);
  }, [open]);

  const handleSubmit = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAccepted();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("wizard.uploadConsent.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || typeof document === "undefined") return null;

  const node = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="presentation"
      aria-hidden={false}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[min(90vh,720px)] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/40 bg-white/95 p-6 shadow-card"
      >
        <h2 id={titleId} className="text-xl font-extrabold text-heading mb-4">
          {t("wizard.uploadConsent.dialogTitle")}
        </h2>
        <label className="flex cursor-pointer gap-3 text-start text-sm text-body leading-relaxed">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-brandOrange focus:ring-brandOrange"
          />
          <span>{t("wizard.uploadConsent.checkboxLabel")}</span>
        </label>
        <p className="mt-4 text-sm">
          <Link
            to="/terms"
            className="font-semibold text-brandNavy underline underline-offset-2 hover:text-brandOrange focus:outline-none focus:ring-2 focus:ring-brandOrange rounded"
          >
            {t("wizard.uploadConsent.viewTerms")}
          </Link>
        </p>
        {error && (
          <p className="mt-3 text-sm text-red-800" role="alert">
            {error}
          </p>
        )}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            disabled={!checked || submitting}
            onClick={handleSubmit}
            className="rounded-2xl bg-brandOrange px-6 py-2.5 font-semibold text-white shadow-soft hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? t("wizard.uploadConsent.submitting") : t("wizard.uploadConsent.submit")}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default ImageUploadConsentDialog;
